import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';

import { getErrorMessage, type PermissionRequestContext } from '@/shared/errors/app-error';
import { PERMISSION_REQUEST_EVENT } from '@/shared/errors/permission-request-events';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';

import { createProjectPermissionRequest } from '../api/api';

type SubmissionState = 'idle' | 'submitting' | 'sent';

export const PermissionRequestDialogHost = () => {
  const [context, setContext] = useState<PermissionRequestContext>();
  const [reason, setReason] = useState('');
  const [submissionState, setSubmissionState] = useState<SubmissionState>('idle');
  const [submissionError, setSubmissionError] = useState<string>();

  useEffect(() => {
    const handlePermissionRequest = (event: Event) => {
      const nextContext = (event as CustomEvent<PermissionRequestContext>).detail;
      setContext(nextContext);
      setReason('');
      setSubmissionError(undefined);
      setSubmissionState(nextContext.requestStatus === 'pending' ? 'sent' : 'idle');
    };
    window.addEventListener(PERMISSION_REQUEST_EVENT, handlePermissionRequest);
    return () => window.removeEventListener(PERMISSION_REQUEST_EVENT, handlePermissionRequest);
  }, []);

  const close = () => {
    setContext(undefined);
    setReason('');
    setSubmissionError(undefined);
    setSubmissionState('idle');
  };

  const submit = async () => {
    if (!context || !context.canRequest || submissionState !== 'idle') return;
    setSubmissionState('submitting');
    setSubmissionError(undefined);
    try {
      await createProjectPermissionRequest(context.projectId, {
        permission_codename: context.permissionCodename,
        reason: reason.trim(),
      });
      setSubmissionState('sent');
    } catch (error) {
      setSubmissionError(getErrorMessage(error));
      setSubmissionState('idle');
    }
  };

  return (
    <Dialog open={Boolean(context)} onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-xl" data-testid="permission-request-dialog">
        <DialogHeader>
          <div className="mb-2 flex size-11 items-center justify-center rounded-[13px] border border-amber-500/30 bg-amber-500/10 text-amber-400">
            <ShieldAlert className="size-5" aria-hidden="true" />
          </div>
          <DialogTitle>Permission required</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            {context?.message}
          </DialogDescription>
        </DialogHeader>

        {context && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{context.projectName}</Badge>
              <Badge variant="outline">{context.permissionLabel}</Badge>
            </div>

            {submissionState === 'sent' ? (
              <div className="rounded-[13px] border border-success/30 bg-success/10 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" />
                  <div>
                    <p className="font-semibold text-foreground">Request sent</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      A project administrator has been notified. You’ll receive a notification when
                      the request is approved or denied.
                    </p>
                  </div>
                </div>
              </div>
            ) : context.canRequest ? (
              <div className="space-y-2">
                <Label htmlFor="permission-request-reason">Why do you need this access?</Label>
                <Textarea
                  id="permission-request-reason"
                  value={reason}
                  maxLength={1000}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Optional: give the administrator context about the work you need to complete."
                />
                <p className="text-xs text-muted-foreground">{reason.length}/1000</p>
              </div>
            ) : (
              <div className="rounded-[13px] border border-border bg-muted/50 p-4 text-sm text-muted-foreground">
                This permission cannot be requested automatically. Contact a project administrator
                to update your project membership.
              </div>
            )}

            {submissionError && (
              <p className="text-sm font-medium text-destructive" role="alert">
                {submissionError}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            {submissionState === 'sent' ? 'Done' : 'Cancel'}
          </Button>
          {context?.canRequest && submissionState !== 'sent' && (
            <Button onClick={() => void submit()} disabled={submissionState === 'submitting'}>
              {submissionState === 'submitting' && <Loader2 className="size-4 animate-spin" />}
              Request access
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
